import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from "react-helmet-async";
import { FaHeart, FaRocket, FaHandshake, FaLightbulb, FaArrowRight } from 'react-icons/fa';
import './AboutPage.css';

const AboutPage = () => {

  const values = [
    {
      icon: <FaHeart />,
      title: 'Passion',
      description: 'We are passionate about technology and innovation.'
    },
    {
      icon: <FaRocket />,
      title: 'Excellence',
      description: 'We deliver high-quality software solutions.'
    },
    {
      icon: <FaHandshake />,
      title: 'Integrity',
      description: 'We build honest and transparent relationships.'
    },
    {
      icon: <FaLightbulb />,
      title: 'Innovation',
      description: 'We use modern technologies to solve real problems.'
    }
  ];

  const team = [
    {
      name: 'Abhishek Yadav',
      position: 'Founder & CEO',
      image: '/abhi.png',
      bio: 'Software developer and business strategist.'
    },
    {
      name: 'Shivam Yadav',
      position: 'CTO',
      image: '/shivam.png',
      bio: 'Expert in cloud architecture and AI.'
    },
    {
      name: 'Anayatuallah',
      position: 'Manager',
      image: '/anayat.png',
      bio: 'Expert in Managing a Team.'
    },
    {
      name: 'Raju Chauhan',
      position: 'Lead Developer',
      image: '/raju.png',
      bio: 'Full-stack developer building scalable apps.'
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="about-page"
    >
      {/* ✅ SEO */}
      <Helmet>
        {/* PRIMARY SEO */}
        <title>About ZustandTech (Zustand Tech Software Private Limited) | Best Software Company in India</title>
        <meta
          name="description"
          content="Learn about ZustandTech (Zustand Tech Software Private Limited), one of the best software development companies in India, specializing in React.js, mobile app development, AI solutions, cloud computing, SaaS platforms, and enterprise software development."
        />
        <meta
          name="keywords"
          content="about ZustandTech, Zustand Tech Software Private Limited, best software company in india, software development company india, React.js development company, AI development company, mobile app development company, cloud computing services, enterprise software solutions, SaaS development company, fullstack development company"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://www.zustandtech.com/about" />

        {/* OPEN GRAPH */}
        <meta property="og:title" content="About ZustandTech | Software Development Company" />
        <meta
          property="og:description"
          content="Global software development company delivering React.js, AI, cloud, SaaS, and enterprise software solutions."
        />
        <meta property="og:image" content="https://www.zustandtech.com/about.png" />
        <meta property="og:url" content="https://www.zustandtech.com/about" />
        <meta property="og:type" content="website" />

        {/* TWITTER SEO */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="About ZustandTech" />
        <meta
          name="twitter:description"
          content="Learn about ZustandTech software development services, team, mission, and technologies."
        />

        {/* ORGANIZATION SCHEMA */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "ZustandTech",
            "legalName": "Zustand Tech Software Private Limited",
            "alternateName": "Zustand Tech Software Private Limited",
            "url": "https://www.zustandtech.com",
            "logo": "https://www.zustandtech.com/ar.png",
            "description": "Global software development company specializing in React.js, AI solutions, cloud computing, SaaS platforms, and mobile app development.",
            "sameAs": [
              "https://www.linkedin.com/company/zustand-tech/",
              "https://www.instagram.com/zustandtech"
            ]
          })}
        </script>
      </Helmet>

      {/* STORY SECTION */}
      <section className="about-story">
        <div className="container">
          <div className="page-heading">
            <h1>
              About ZustandTech - <span className="gradient-text">Software Development Company in India</span>
            </h1>
            <p>
              ZustandTech (registered as <strong>Zustand Tech Software Private Limited</strong>) is a
              modern software development company helping startups and businesses build web
              applications, mobile apps, AI solutions and cloud systems.
            </p>
          </div>

          <div className="story-grid">
            <motion.div 
              className="story-content"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="section-title">Our Story</h2>
              <p>
                Founded in 2020, ZustandTech started with a mission to help businesses grow using technology.
                Today, we're recognized as one of the best software companies in India, delivering scalable
                web, mobile, AI and cloud solutions worldwide.
              </p>
              <p>
                We have successfully completed multiple projects and continue to innovate 
                with modern technologies to deliver the best results.
              </p>
            </motion.div>

            <motion.div 
              className="story-image"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <img 
                src="/about.png"
                alt="Software development team working at ZustandTech"
                loading="lazy"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* SEO CONTENT */}
      <section className="about-seo-content">
        <div className="container">
          <h2>Global Software Development Company</h2>
          <p>
            ZustandTech is a modern software development company
            helping startups and enterprises build scalable
            web applications, mobile apps, AI systems,
            SaaS platforms, and cloud-native software solutions.
          </p>

          <h2>React.js, AI & Cloud Experts</h2>
          <p>
            Our team specializes in React.js development,
            Node.js backend systems, AI automation,
            cloud infrastructure, DevOps,
            enterprise software development,
            and digital transformation solutions.
          </p>
        </div>
      </section>

      {/* STATS */}
      <section className="about-stats">
        <div className="container">
          <div className="stats-grid">
            {[
              { number: '100+', label: 'Projects Delivered' },
              { number: '50+', label: 'Expert Developers' },
              { number: '5+', label: 'Years Experience' },
              { number: '98%', label: 'Client Satisfaction' }
            ].map((stat, index) => (
              <motion.div 
                key={index} 
                className="stat-item"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <h3>{stat.number}</h3>
                <p>{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="about-values">
        <div className="container">
          <h2 className="section-title">Our <span className="gradient-text">Core Values</span></h2>

          <div className="values-grid">
            {values.map((value, index) => (
              <motion.div 
                key={index} 
                className="value-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="value-icon">{value.icon}</div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="about-team">
        <div className="container">
          <h2 className="section-title">Meet Our <span className="gradient-text">Team</span></h2>

          <div className="team-grid">
            {team.map((member, index) => (
              <motion.div 
                key={index} 
                className="team-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.15 }}
                viewport={{ once: true }}
              >
                <div className="team-image">
                  <img src={member.image} alt={member.name} loading="lazy" />
                </div>
                <h3>{member.name}</h3>
                <p className="team-position">{member.position}</p>
                <p className="team-bio">{member.bio}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="page-cta-section">
        <div className="container">
          <div className="page-cta-content">
            <h2>Start Your Project Today</h2>
            <h3>Let's Build Something Amazing</h3>
            <p>Contact ZustandTech for web, mobile, AI and cloud solutions.</p>

            <Link to="/contact" className="cta-button">
              Contact Us <FaArrowRight />
            </Link>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default AboutPage;